import { useState, useEffect, useRef } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { ScrollArea } from '@/components/ui/scroll-area'
import { CheckCircle } from 'lucide-react'
import type { ArtistOnboarding } from '@/types/models'
import { ARTIST_AGREEMENT, ARTIST_AGREEMENT_KEY_POINTS } from '@/constants/agreements'

interface ArtistOnboardingStep4Props {
  onboarding: ArtistOnboarding
  onDataChange: (data: Partial<ArtistOnboarding>) => void
}

export function ArtistOnboardingStep4({ onboarding, onDataChange }: ArtistOnboardingStep4Props) {
  const [agreementAccepted, setAgreementAccepted] = useState(onboarding?.agreementAccepted || false)
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false)
  const scrollAreaRef = useRef<HTMLDivElement>(null)

  // Initialize state from onboarding prop when it changes
  useEffect(() => {
    if (onboarding?.agreementAccepted !== undefined) {
      setAgreementAccepted(onboarding.agreementAccepted)
    }
  }, [onboarding?.agreementAccepted])

  // Update parent component when agreement acceptance changes
  useEffect(() => {
    onDataChange({ agreementAccepted })
  }, [agreementAccepted]) // Remove onDataChange from dependencies to prevent infinite loop

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget
    const scrolledToBottom = scrollTop + clientHeight >= scrollHeight - 10 // 10px tolerance
    
    if (scrolledToBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true)
    }
  }

  const handleAcceptanceChange = (checked: boolean) => {
    setAgreementAccepted(checked)
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-semibold">
              5
            </div>
            Artist Agreement
          </CardTitle>
          <CardDescription>
            Please read the GetMoreTats Artist Agreement carefully before accepting the terms.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Scrollable Agreement Text */}
          <div className="border rounded-lg">
            <ScrollArea 
              className="h-96 p-4"
              onScrollCapture={handleScroll}
              ref={scrollAreaRef}
            >
              <div className="whitespace-pre-line text-sm leading-relaxed">
                {ARTIST_AGREEMENT}
              </div>
            </ScrollArea>
          </div>

          {/* Scroll Indicator */}
          {!hasScrolledToBottom && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
              Please scroll down to read the complete agreement before accepting
            </div>
          )}

          {/* Acceptance Checkbox */}
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <Checkbox
                id="agreement-acceptance"
                checked={agreementAccepted}
                onCheckedChange={handleAcceptanceChange}
                className="mt-1"
              />
              <div className="space-y-1">
                <label
                  htmlFor="agreement-acceptance"
                  className="text-sm font-medium leading-none cursor-pointer"
                >
                  I accept the GetMoreTats Artist Agreement
                </label>
                <p className="text-xs text-muted-foreground">
                  By checking this box, you agree to be bound by the terms and conditions outlined above.
                </p>
              </div>
            </div>

            {/* Status Indicator */}
            {agreementAccepted && (
              <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">
                <CheckCircle className="w-4 h-4" />
                Agreement accepted! You can now publish your profile.
              </div>
            )}
          </div>

          {/* Agreement Summary */}
          <Card className="bg-muted/50">
            <CardContent className="pt-6">
              <h4 className="font-semibold mb-2">Key Points Summary:</h4>
              <ul className="text-sm space-y-1 text-muted-foreground">
                {ARTIST_AGREEMENT_KEY_POINTS.map(point => (
                  <li key={point.title}>• <strong>{point.title}:</strong> {point.description}</li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </CardContent>
      </Card>
    </div>
  )
}
